import { useCallback, useEffect, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Loader2,
  MapPin,
  Minus,
  Package,
  Plus,
  X,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { useToast } from "~/hooks/use-toast";
import { useReceivedAwaitingPlacement } from "~/hooks/wms/useIngestion";
import { ingestionService } from "~/services/wms/ingestionService";
import { useProfileStore } from "~/store/profileStore";
import type { IngestionRequest } from "~/types/wms";

type WorkflowStep = "select-batch" | "scan-position" | "confirm" | "completed";

export function PlacementWorkflowPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const profileId = useProfileStore((s) => s.id);
  const { requestId: routeRequestId } = useParams<{ requestId?: string }>();

  // State
  const [currentStep, setCurrentStep] = useState<WorkflowStep>("select-batch");
  const [selectedRequest, setSelectedRequest] =
    useState<IngestionRequest | null>(null);
  const [positionInput, setPositionInput] = useState("");
  const [positionId, setPositionId] = useState<string | null>(null);
  const [positionCode, setPositionCode] = useState<string>("");
  const [boxCount, setBoxCount] = useState(0);
  const [completedRequest, setCompletedRequest] =
    useState<IngestionRequest | null>(null);

  // Refs
  const positionInputRef = useRef<HTMLInputElement>(null);

  // Query for batches awaiting placement
  const { data: awaitingPlacement = [], isLoading: isLoadingBatches } =
    useReceivedAwaitingPlacement();

  // If route has requestId, auto-select
  useEffect(() => {
    if (routeRequestId && awaitingPlacement.length > 0 && !selectedRequest) {
      const found = awaitingPlacement.find((r) => r.id === routeRequestId);
      if (found) {
        handleSelectBatch(found);
      }
    }
  }, [routeRequestId, awaitingPlacement, selectedRequest]);

  // Auto-focus on position input when step changes
  useEffect(() => {
    if (currentStep === "scan-position") {
      setTimeout(() => positionInputRef.current?.focus(), 100);
    }
  }, [currentStep]);

  // Lookup position mutation
  const lookupPositionMutation = useMutation({
    mutationFn: (input: string) =>
      ingestionService.lookupPositionByBarcode(input),
    onSuccess: (result) => {
      if (result.position) {
        setPositionId(result.position.id);
        setPositionCode(result.position.full_code || result.position.code);
        setCurrentStep("confirm");
      } else {
        toast({
          title: "Posicion no encontrada",
          description: result.error || "No se encontro la posicion",
          variant: "destructive",
        });
        setPositionInput("");
        positionInputRef.current?.focus();
      }
    },
    onError: (err) => {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Error al buscar posicion",
        variant: "destructive",
      });
      setPositionInput("");
      positionInputRef.current?.focus();
    },
  });

  // Place batch mutation
  const placeBatchMutation = useMutation({
    mutationFn: ({
      requestId,
      positionId,
      boxCount,
      userId,
    }: {
      requestId: string;
      positionId: string;
      boxCount: number;
      userId: string;
    }) =>
      ingestionService.placeBatchInPosition(
        requestId,
        positionId,
        boxCount,
        userId
      ),
    onSuccess: (result) => {
      if (result.success && result.request) {
        setCompletedRequest(result.request);
        queryClient.invalidateQueries({ queryKey: ["wms_ingestion_requests"] });
        setCurrentStep("completed");
        toast({
          title: "Ubicacion completada",
          description: `Lote ubicado en ${positionCode}`,
        });
      } else {
        toast({
          title: "Error",
          description: result.error || "No se pudo ubicar el lote",
          variant: "destructive",
        });
      }
    },
    onError: (err) => {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Error al ubicar lote",
        variant: "destructive",
      });
    },
  });

  // Handlers
  const handleSelectBatch = (request: IngestionRequest) => {
    setSelectedRequest(request);
    setBoxCount(request.expected_boxes || 0);
    setCurrentStep("scan-position");
  };

  const handlePositionSubmit = useCallback(() => {
    if (!positionInput.trim()) return;
    const input = positionInput.trim().replace(/^LOC:/i, "");
    lookupPositionMutation.mutate(input);
  }, [positionInput, lookupPositionMutation]);

  const handlePositionKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handlePositionSubmit();
    }
  };

  const handleConfirmPlacement = () => {
    if (!selectedRequest || !positionId || !profileId) return;
    placeBatchMutation.mutate({
      requestId: selectedRequest.id,
      positionId,
      boxCount,
      userId: profileId,
    });
  };

  const handleAdjustBoxCount = (delta: number) => {
    setBoxCount((prev) => Math.max(0, prev + delta));
  };

  const resetFlow = () => {
    setCurrentStep("select-batch");
    setSelectedRequest(null);
    setPositionInput("");
    setPositionId(null);
    setPositionCode("");
    setBoxCount(0);
    setCompletedRequest(null);
  };

  const goBackToPosition = () => {
    setCurrentStep("scan-position");
    setPositionInput("");
    setPositionId(null);
    setPositionCode("");
  };

  // Render step content
  const renderStep = () => {
    switch (currentStep) {
      case "select-batch":
        return (
          <SelectBatchStep
            batches={awaitingPlacement}
            isLoading={isLoadingBatches}
            onSelect={handleSelectBatch}
            onBack={() => navigate("/wms")}
          />
        );

      case "scan-position":
        return (
          <ScanPositionStep
            request={selectedRequest!}
            inputRef={positionInputRef}
            value={positionInput}
            onChange={setPositionInput}
            onKeyPress={handlePositionKeyPress}
            onSubmit={handlePositionSubmit}
            onCancel={resetFlow}
            isLoading={lookupPositionMutation.isPending}
          />
        );

      case "confirm":
        return (
          <ConfirmStep
            request={selectedRequest!}
            positionCode={positionCode}
            boxCount={boxCount}
            expectedBoxes={selectedRequest?.expected_boxes || 0}
            onAdjustBoxCount={handleAdjustBoxCount}
            onConfirm={handleConfirmPlacement}
            onBack={goBackToPosition}
            isLoading={placeBatchMutation.isPending}
          />
        );

      case "completed":
        return (
          <CompletedStep
            request={completedRequest!}
            positionCode={positionCode}
            boxCount={boxCount}
            onPlaceAnother={resetFlow}
            onBackToMenu={() => navigate("/wms")}
          />
        );
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Ubicacion de Lotes"
        description={
          currentStep === "select-batch"
            ? "Selecciona un lote para ubicar"
            : currentStep === "scan-position"
              ? "Escanea la posicion de destino"
              : currentStep === "confirm"
                ? "Confirma la ubicacion"
                : "Ubicacion completada"
        }
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          (currentStep === "scan-position" || currentStep === "confirm") && (
            <Button variant="outline" onClick={resetFlow}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
          )
        }
      />

      <div className="mx-auto max-w-2xl">{renderStep()}</div>
    </PageContainer>
  );
}

// ============================================================================
// Select Batch Step
// ============================================================================

function SelectBatchStep({
  batches,
  isLoading,
  onSelect,
  onBack,
}: {
  batches: IngestionRequest[];
  isLoading: boolean;
  onSelect: (request: IngestionRequest) => void;
  onBack: () => void;
}) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center gap-2">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-52" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-40" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-5 w-5 shrink-0 rounded-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Card className="w-full max-w-sm">
          <CardContent className="flex flex-col items-center gap-6 py-12">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
              <Package className="h-10 w-10 text-muted-foreground" />
            </div>
            <div className="text-center">
              <h2 className="text-xl font-semibold">Sin Lotes Pendientes</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                No hay lotes recibidos pendientes de ubicacion
              </p>
            </div>
            <Button variant="outline" className="w-full" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver al Menu
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h2 className="text-lg font-semibold">Lotes Pendientes de Ubicacion</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Selecciona un lote para asignarle una posicion
        </p>
      </div>

      <div className="space-y-3">
        {batches.map((batch) => (
          <BatchCard
            key={batch.id}
            batch={batch}
            onSelect={() => onSelect(batch)}
          />
        ))}
      </div>

      <Button
        variant="ghost"
        className="w-full text-muted-foreground"
        onClick={onBack}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Volver al Menu
      </Button>
    </div>
  );
}

function BatchCard({
  batch,
  onSelect,
}: {
  batch: IngestionRequest;
  onSelect: () => void;
}) {
  const batchNumber = batch.production_batch?.batch_number || "—";
  const productName = batch.product?.short_name || "—";
  const expiryDate = batch.production_batch?.expiry_date
    ? dayjs(batch.production_batch.expiry_date).format("DD/MM/YYYY")
    : "—";
  const verifiedAt = batch.verified_at
    ? dayjs(batch.verified_at).format("DD/MM HH:mm")
    : "—";

  return (
    <Card
      className="cursor-pointer transition-colors hover:border-primary hover:bg-primary/5"
      onClick={onSelect}
    >
      <CardContent className="flex items-center justify-between p-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <span className="font-mono font-medium">{batchNumber}</span>
          </div>
          <p className="mt-1 truncate text-sm text-muted-foreground">
            {productName}
          </p>
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span>{batch.expected_boxes} cajas</span>
            <span>Vence: {expiryDate}</span>
            <span>Recibido: {verifiedAt}</span>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground" />
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Scan Position Step
// ============================================================================

function ScanPositionStep({
  request,
  inputRef,
  value,
  onChange,
  onKeyPress,
  onSubmit,
  onCancel,
  isLoading,
}: {
  request: IngestionRequest;
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const batchNumber = request.production_batch?.batch_number || "—";
  const productName = request.product?.short_name || "—";

  return (
    <div className="space-y-6">
      {/* Selected batch info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <h3 className="text-sm font-medium text-blue-700">
            Lote Seleccionado
          </h3>
          <p className="mt-1 font-mono font-medium">{batchNumber}</p>
          <p className="text-sm text-muted-foreground">{productName}</p>
          <p className="text-sm text-muted-foreground">
            {request.expected_boxes} cajas esperadas
          </p>
        </CardContent>
      </Card>

      {/* Position scan */}
      <div className="text-center">
        <h2 className="text-lg font-semibold">Escanear Posicion</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Escanea o ingresa el codigo de la posicion de destino
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyPress={onKeyPress}
                placeholder="Ej: A-01-01-1"
                className="pl-10 pr-10 text-center font-mono text-lg"
                disabled={isLoading}
                autoFocus
              />
              {value && (
                <button
                  type="button"
                  onClick={() => onChange("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button
              className="w-full"
              onClick={onSubmit}
              disabled={!value.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Buscando...
                </>
              ) : (
                "Confirmar Posicion"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Button
        variant="ghost"
        size="sm"
        className="w-full text-muted-foreground"
        onClick={onCancel}
        disabled={isLoading}
      >
        Cancelar
      </Button>
    </div>
  );
}

// ============================================================================
// Confirm Step
// ============================================================================

function ConfirmStep({
  request,
  positionCode,
  boxCount,
  expectedBoxes,
  onAdjustBoxCount,
  onConfirm,
  onBack,
  isLoading,
}: {
  request: IngestionRequest;
  positionCode: string;
  boxCount: number;
  expectedBoxes: number;
  onAdjustBoxCount: (delta: number) => void;
  onConfirm: () => void;
  onBack: () => void;
  isLoading: boolean;
}) {
  const batchNumber = request.production_batch?.batch_number || "—";
  const productName = request.product?.short_name || "—";
  const hasDiscrepancy = boxCount !== expectedBoxes;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-semibold">Confirmar Ubicacion</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Verifica los datos antes de confirmar
        </p>
      </div>

      {/* Summary card */}
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Lote</span>
            <span className="font-mono font-medium">{batchNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Producto</span>
            <span className="font-medium">{productName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Posicion</span>
            <span className="font-mono font-medium text-primary">
              {positionCode}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Box count adjuster */}
      <Card className={hasDiscrepancy ? "border-yellow-300 bg-yellow-50" : ""}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Cantidad de Cajas</p>
              <p className="text-sm text-muted-foreground">
                Esperadas: {expectedBoxes}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => onAdjustBoxCount(-1)}
                disabled={boxCount <= 0 || isLoading}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="min-w-[3rem] text-center text-xl font-bold">
                {boxCount}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => onAdjustBoxCount(1)}
                disabled={isLoading}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {hasDiscrepancy && (
            <p className="mt-2 text-sm text-yellow-700">
              Diferencia de {Math.abs(boxCount - expectedBoxes)} cajas
            </p>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <Button className="w-full" onClick={onConfirm} disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Ubicando...
            </>
          ) : (
            <>
              <Check className="mr-2 h-4 w-4" />
              Confirmar Ubicacion
            </>
          )}
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={onBack}
          disabled={isLoading}
        >
          Cambiar Posicion
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Completed Step
// ============================================================================

function CompletedStep({
  request,
  positionCode,
  boxCount,
  onPlaceAnother,
  onBackToMenu,
}: {
  request: IngestionRequest;
  positionCode: string;
  boxCount: number;
  onPlaceAnother: () => void;
  onBackToMenu: () => void;
}) {
  const batchNumber = request.production_batch?.batch_number || "—";
  const productName = request.product?.short_name || "—";

  return (
    <div className="space-y-6 py-8">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold">Ubicacion Completada</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          El lote ha sido ubicado exitosamente
        </p>
      </div>

      <Card className="border-green-200 bg-green-50">
        <CardContent className="space-y-3 p-4">
          <h3 className="font-medium text-green-700">Resumen</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Lote</span>
              <span className="font-mono font-medium">{batchNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Producto</span>
              <span className="font-medium">{productName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Posicion</span>
              <span className="font-mono font-medium text-green-700">
                {positionCode}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Cajas Ubicadas</span>
              <span className="font-medium">{boxCount}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3">
        <Button className="w-full" onClick={onPlaceAnother}>
          <Package className="mr-2 h-4 w-4" />
          Ubicar Otro Lote
        </Button>
        <Button variant="outline" className="w-full" onClick={onBackToMenu}>
          Volver al Menu
        </Button>
      </div>
    </div>
  );
}

export default PlacementWorkflowPage;
